import {
    CELL_STYLE, TEMPORARY_CELL, CELL_HOVER_STYLE, ENDPOINT_RADIUS, CELL_HOVER_INVALID_STYLE
} from "./grid-cell.config";
import { GRID_DIRECTION } from "../../const/grid-directions.const";
import { GridConfig } from "../../modules/GridConfig";

/**
 * @description returns the grid cell container config
 * @param {Object} contentConfig  - container config
 * @param {Object} layout - cell layout
 */
export function getContainerConfig(contentConfig, layout) {
    let x; let y; let width; let height;
    const radius = contentConfig.contentContainerRadius;
    const widthPercentage = contentConfig.contentContainerWidthPercentage;
    const heightPercentage = contentConfig.contentContainerHeightPercentage;
    width = layout.width * widthPercentage / 100;
    height = layout.height * heightPercentage / 100;
    // calculate equal padding
    x = layout.x + ((layout.width - width) / 2);
    y = layout.y + ((layout.height - height) / 2);
    return {
        x,
        y,
        width,
        height,
        radius
    };
}
/**
 * @description returns the grid cell content config
 * @param {Object} contentConfig  - content config
 * @param {Object} layout - cell layout
 * @param {Object} ctx - canvas 2d context
 */
export function getContentConfig(contentConfig, layout, ctx) {
    let x; let y; let width; let height;
    const widthPercentage = contentConfig.contentContainerWidthPercentage;
    const heightPercentage = contentConfig.contentContainerHeightPercentage;
    width = layout.width * widthPercentage / 100;
    height = layout.height * heightPercentage / 100;
    // calculate equal padding
    x = layout.x + ((layout.width - width) / 2);
    y = layout.y + ((layout.height - height) / 2);
    return {
        options: {
            position: {
                x,
                y
            },
            size: {
                width,
                height
            }
        },
        ctx
    };
}
/**
 * @descriptino Gets cell Style based on the state
 * @param {Object} state
 * @param {Boolean} hasContent
 */
export function getCellStyle(state, hasContent) {
    if (state.dragHover && hasContent && !state.selected) return CELL_HOVER_INVALID_STYLE;
    if (state.dragHover) return CELL_HOVER_STYLE;
    if (state.temporary) return TEMPORARY_CELL;
    return CELL_STYLE;
}
/**
 * @description gets cell position indexes inside the generatedGrid matrix
 */
export function getCellsPositionFromCells(cells, config) {
    return cells.map((cell) => (!cell ? null : {
        row: (cell.layout.y - config.viewPadding) / config.cellHeight,
        col: (cell.layout.x - config.viewPadding) / config.cellWidth
    })).filter(cell => cell !== null);
}

/**
 * @description gets draggable endpoint positions
 * @param {Object} container - content container instance
 * @param {String} dir - the direction the mouse is currently hovering at
 */
export function getDraggableEndpointPosition(container, dir) {
    const position = { x: null, y: null };
    const { layout } = container;
    if (!dir) {
        position.x = layout.x + (layout.width / 2);
        position.y = layout.y + (layout.height / 2);
        return position;
    }
    if (dir === GRID_DIRECTION.TOP) {
        position.x = layout.x + (layout.width / 2);
        position.y = layout.y;
    }
    if (dir === GRID_DIRECTION.BOTTOM) {
        position.x = layout.x + (layout.width / 2);
        position.y = layout.y + layout.height;
    }
    if (dir === GRID_DIRECTION.LEFT) {
        position.x = layout.x;
        position.y = layout.y + (layout.height / 2);
    }
    if (dir === GRID_DIRECTION.RIGHT) {
        position.x = layout.x + layout.width;
        position.y = layout.y + (layout.height / 2);
    }
    if (dir === GRID_DIRECTION.TOP_LEFT) {
        position.x = layout.x;
        position.y = layout.y;
    }
    if (dir === GRID_DIRECTION.TOP_RIGHT) {
        position.x = layout.x + layout.width;
        position.y = layout.y;
    }
    if (dir === GRID_DIRECTION.BOTTOM_RIGHT) {
        position.x = layout.x + layout.width;
        position.y = layout.y + layout.height;
    }
    if (dir === GRID_DIRECTION.BOTTOM_LEFT) {
        position.x = layout.x;
        position.y = layout.y + layout.height;
    }
    return position;
}

/**
 * @description Finds the drop endpoint of a connecting line drop over a cell
 * @param {Object} mousePos - mouse position
 * @param {Object} endpoints  - Possible endpoints
 * @param {Number} endpointRadius  - endpoint radius
 * @param {String} instanceUuid  - diagram instance uuid
 */
export function findDropEndpoint(mousePos, endpoints, endpointRadius, instanceUuid) {
    const endpointKeys = Object.keys(endpoints);
    const gridConfig = new GridConfig(instanceUuid);
    const r = endpointRadius || ENDPOINT_RADIUS;
    // if (r > (gridConfig.cellWidth / 2)) return null;
    for (let i = 0; i < endpointKeys.length; i++) {
        const ep = endpoints[endpointKeys[i]];
        const foundIt = pointInCircle(mousePos.x, mousePos.y, ep.x, ep.y, r);
        if (foundIt) {
            return { side: endpointKeys[i], endpoint: ep };
        }
    }
    return findDropEndpoint(mousePos, endpoints, (r + 2), instanceUuid);
}
/**
 * @description returns all possible endpoint positions
 * @param {Object} container - cell content container
 * @param {Boolean} endpointCircleEnd - indicates if it should adjust the endpoints position with the radius
 * @param {GridCell} cell - cell ref
 */
export function getPossibleEndpointPositions(container, endpointCircleEnd, cell) {
    const returnObj = {}; const
        layout = container ? container.layout : getContainerConfig(cell.content.config.container, cell.layout);
    returnObj[GRID_DIRECTION.LEFT] = { x: layout.x, y: layout.y + (layout.height / 2) };
    returnObj[GRID_DIRECTION.RIGHT] = { x: layout.x + layout.width, y: layout.y + (layout.height / 2) };
    returnObj[GRID_DIRECTION.BOTTOM] = { x: layout.x + (layout.width / 2), y: layout.y + layout.height };
    returnObj[GRID_DIRECTION.TOP] = { x: layout.x + (layout.width / 2), y: layout.y };
    if (endpointCircleEnd) {
        const r = ENDPOINT_RADIUS;
        returnObj[GRID_DIRECTION.LEFT].x -= r;
        returnObj[GRID_DIRECTION.RIGHT].x += r;
        returnObj[GRID_DIRECTION.BOTTOM].y += r;
        returnObj[GRID_DIRECTION.TOP].y -= r;
    }
    return returnObj;
}

// x,y is the point to test
// cx, cy is circle center, and radius is circle radius
export function pointInCircle(x, y, cx, cy, radius) {
    const distancesquared = (x - cx) * (x - cx) + (y - cy) * (y - cy);
    return distancesquared <= radius * radius;
}

export function pointInRectangle(rect, point) {
    return rect.x <= point.x && point.x <= rect.x + rect.width
        && rect.y <= point.y && point.y <= rect.y + rect.height;
}

function pointCircleCollision(point, circle, r) {
    if (r === 0) return false;
    const dx = circle[0] - point[0];
    const dy = circle[1] - point[1];
    return dx * dx + dy * dy <= r * r;
}

export function lineCircleCollide(a, b, circle, radius, nearest) {
    const tmp = [0, 0];

    // check to see if start or end points lie within circle
    if (pointCircleCollision(a, circle, radius)) {
        if (nearest) {
            nearest[0] = a[0];
            nearest[1] = a[1];
        }
        return true;
    }
    if (pointCircleCollision(b, circle, radius)) {
        if (nearest) {
            nearest[0] = b[0];
            nearest[1] = b[1];
        }
        return true;
    }

    const x1 = a[0];
    const y1 = a[1];
    const x2 = b[0];
    const y2 = b[1];
    const cx = circle[0];
    const cy = circle[1];

    // vector d
    const dx = x2 - x1;
    const dy = y2 - y1;

    // vector lc
    const lcx = cx - x1;
    const lcy = cy - y1;

    // project lc onto d, resulting in vector p
    const dLen2 = dx * dx + dy * dy; // len2 of d
    let px = dx;
    let py = dy;
    if (dLen2 > 0) {
        const dp = (lcx * dx + lcy * dy) / dLen2;
        px *= dp;
        py *= dp;
    }

    if (!nearest) { nearest = tmp; }
    nearest[0] = x1 + px;
    nearest[1] = y1 + py;

    // len2 of p
    const pLen2 = px * px + py * py;

    // check collision
    return pointCircleCollision(nearest, circle, radius)
        && pLen2 <= dLen2 && (px * dx + py * dy) >= 0;
}

export function circleLineIntersection(ax, ay, bx, by, cx, cy, r) {
    // put circle at the center to simplify calcs
    ax -= cx; ay -= cy;
    bx -= cx; by -= cy;

    const c = ax ^ 2 + ay ^ 2 - r ^ 2;
    const b = 2 * (ax * (bx - ax) + ay * (by - ay));
    const a = (bx - ax) ^ 2 + (by - ay) ^ 2;

    // get discriminant
    const disc = b ^ 2 - 4 * a * c;

    // check if discriminant has real values
    if (disc <= 0) return false;

    // find intersection points
    const sqrtdisc = Math.sqrt(disc);
    const t1 = (-b + sqrtdisc) / (2 * a);
    const t2 = (-b - sqrtdisc) / (2 * a);
    if (t1 > 0 && t1 < 1 && t2 > 0 && t2 < 1) return true;
    return false;
}
